import type { ReactNode } from "react";
import {
  ChevronDown,
  CircleCheck,
  CloudOff,
  Crosshair,
  Download,
  List,
  Lock,
  Map as MapIcon,
  Radio,
  RefreshCw,
  Search,
  Share2,
  Trash2,
  UserPlus,
  UserRound,
  Users,
  Wrench,
} from "lucide-react";
import { UtilityIcon } from "../utility/UtilityIcon.js";
import {
  Chip,
  Select,
  Frames,
  Radar,
  Row,
  route,
  routePoints,
} from "./illustration-parts.js";
import type { IllustrationKey } from "./onboarding-steps.js";

/** Mini-Ansichten für Quickview und Granatenkatalog. */
export const quickviewScenes: Partial<
  Record<IllustrationKey, () => ReactNode>
> = {
  welcome: () => (
    <div className="ob-stack">
      <span className="ob-bar">
        <Crosshair className="ob-accent" />
        <b>CS2</b>
        <Chip tone="primary">Quickview</Chip>
        <Chip>Editor</Chip>
        <Chip>Katalog</Chip>
        <span className="ob-grow" />
        <Chip hl>ROLLE</Chip>
        <span className="ob-avatar ob-hl">
          <UserRound />
          <ChevronDown />
        </span>
      </span>
      <span className="ob-lines" />
    </div>
  ),
  select: () => (
    <div className="ob-stack">
      <Row>
        <Select label="MAP" value="Mirage" hl />
        <Select label="KATEGORIE" value="Default" />
        <Select label="TAKTIK" value="A-Execute" />
      </Row>
      <span className="ob-segment">
        <span className="is-on ob-hl">T-Seite</span>
        <span>CT-Seite</span>
      </span>
    </div>
  ),
  slots: () => (
    <div className="ob-stack">
      <span className="ob-tabs">
        {["P1", "P2", "P3", "P4", "P5"].map((slot) => (
          <span key={slot} className={slot === "P3" ? "is-on ob-hl" : ""}>
            {slot}
            <small>{slot === "P3" ? "ENTRY" : "ROLLE"}</small>
          </span>
        ))}
      </span>
      <span className="ob-lines" />
    </div>
  ),
  steps: () => (
    <Radar
      overlay={
        <span className="ob-corner">
          <Chip tone="primary">▶ Abspielen</Chip>
        </span>
      }
    >
      <path d={route} className="ob-route" />
      {routePoints.slice(1).map(([x, y], index) => (
        <g key={index}>
          <circle
            cx={x}
            cy={y}
            r={index === 1 ? 8 : 6}
            className={index === 1 ? "ob-node is-hl" : "ob-node"}
          />
          <text x={x} y={y + 3} className="ob-node-label">
            {index + 1}
          </text>
        </g>
      ))}
    </Radar>
  ),
  utility: () => (
    <Row>
      <span className="ob-card ob-hl">
        <UtilityIcon type="SMOKE" />
        <span>
          <b>SMOKE</b>
          <small>Jungle</small>
        </span>
      </span>
      <span className="ob-arrow">→</span>
      <Frames />
    </Row>
  ),
  status: () => (
    <div className="ob-stack">
      <Row>
        <Chip tone="route">Veröffentlicht</Chip>
        <Chip tone="warning">
          <CloudOff /> LOKALE VORSCHAU
        </Chip>
      </Row>
      <Row>
        <Chip tone="accent" hl>
          Neue Version · Rev 12
        </Chip>
        <span className="ob-icon-btn">
          <RefreshCw />
        </span>
        <span className="ob-icon-btn">
          <Download />
        </span>
      </Row>
    </div>
  ),
  "live-follow": () => (
    <div className="ob-stack">
      <Row>
        <Chip tone="route">
          <Radio /> Captain live
        </Chip>
        <span className="ob-arrow">→</span>
        <span className="ob-avatar">
          <UserRound />
        </span>
      </Row>
      <span className="ob-toast ob-hl">DU BIST P2</span>
    </div>
  ),
  "live-lead": () => (
    <div className="ob-stack">
      <Chip tone="accent" hl>
        <Radio /> Live · Du steuerst
      </Chip>
      <span className="ob-panel">
        <small>POSITIONEN ZUORDNEN</small>
        <Row>
          <Chip>anna@…</Chip>
          <Chip>ben@…</Chip>
          <span className="ob-arrow">→</span>
          <Chip tone="primary">P4</Chip>
        </Row>
      </span>
    </div>
  ),
  team: () => (
    <div className="ob-stack">
      <span className="ob-team ob-hl">
        <Users /> Mein Team <i className="ob-dot is-on" /> 3
      </span>
      <span className="ob-panel">
        <Row>
          <UserRound /> anna <Chip tone="route">QUICKVIEW</Chip>
        </Row>
        <Row>
          <UserRound /> ben <Chip tone="muted">OFFLINE</Chip>
        </Row>
      </span>
    </div>
  ),
  "team-manage": () => (
    <div className="ob-stack">
      <span className="ob-panel">
        <Row>
          <UserRound /> ben
          <span className="ob-grow" />
          <Select label="ROLLE" value="EDITOR" hl />
          <Chip tone="route">AKTIV</Chip>
        </Row>
      </span>
      <Row>
        <span className="ob-input">NEUES MITGLIED</span>
        <Chip tone="primary" hl>
          <UserPlus /> EINLADEN
        </Chip>
      </Row>
    </div>
  ),
  "catalog-browse": () => (
    <div className="ob-stack">
      <Row>
        <span className="ob-input">
          <Search /> Granaten suchen…
        </span>
        <span className="ob-segment is-small">
          <span>
            <List /> Liste
          </span>
          <span className="is-on ob-hl">
            <MapIcon /> Karte
          </span>
        </span>
      </Row>
      <Row>
        <Chip>
          <UtilityIcon type="SMOKE" /> 12
        </Chip>
        <Chip>
          <UtilityIcon type="FLASH" /> 8
        </Chip>
        <Chip>
          <UtilityIcon type="MOLLY" /> 4
        </Chip>
      </Row>
    </div>
  ),
  "catalog-modes": () => (
    <Row>
      <span className="ob-segment is-small">
        <span>
          <Wrench /> Arbeiten
        </span>
        <span className="is-on ob-hl">
          <Crosshair /> Ingame
        </span>
      </span>
      <span className="ob-arrow">→</span>
      <Frames />
    </Row>
  ),
  "catalog-filters": () => (
    <div className="ob-stack">
      <Row>
        <Select label="TAKTIK" value="A-Split" />
        <span className="ob-segment is-small">
          <span className="is-on">Meine</span>
          <span>Team</span>
          <span>Privat</span>
        </span>
      </Row>
      <Row>
        <Chip hl>A SITE 5</Chip>
        <Chip>JUNGLE 3</Chip>
        <Chip>CT 2</Chip>
      </Row>
    </div>
  ),
  "catalog-details": () => (
    <Row>
      <Chip tone="primary" hl>
        DETAILS
      </Chip>
      <span className="ob-arrow">→</span>
      <Frames />
    </Row>
  ),
  "catalog-create": () => (
    <Radar
      overlay={
        <span className="ob-corner">
          <Chip hl>START SETZEN</Chip>
          <Chip>ZIEL SETZEN</Chip>
        </span>
      }
    >
      <path d="M75 70 Q150 3 230 40" className="ob-arc" />
      <circle cx="75" cy="70" r="6" className="ob-node is-hl" />
      <circle cx="230" cy="40" r="6" className="ob-node" />
    </Radar>
  ),
  "catalog-share": () => (
    <Row>
      <Chip tone="muted">
        <Lock /> PRIVAT
      </Chip>
      <span className="ob-arrow">→</span>
      <Chip tone="primary" hl>
        <Share2 /> TEILEN
      </Chip>
      <Chip>
        <Trash2 /> LÖSCHEN
      </Chip>
      <CircleCheck className="ob-accent" />
    </Row>
  ),
};
